import { useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/StudentRosterTable.scss";

/**
 * StudentRosterTable component displays a sortable and filterable table of students
 * associated with a selected grading profile. It allows teachers to remove students or
 * clear the entire roster.
 *
 * @component
 * @param {Function} dispatch
 * @param {Array} gradingProfileStudents
 * @param {Function} handleCheckboxChange
 * @param {Bool} isGroup
 * @param {Object} selectedEpr
 * @param {Array} selectedStudents
 * @param {Function} setDeltaStudents
 * @param {Function} setShowSectionSelect
 * @param {Function} setStudentsNotAdded
 * @param {Object} state
 * @returns {JSX.Element}
 */
const StudentRosterTable = ({
    dispatch,
    gradingProfileStudents,
    isActiveAndIsUnreleased,
    handleCheckboxChange,
    isGroup,
    selectedEpr,
    selectedStudents,
    setDeltaStudents,
    setShowSectionSelect,
    setStudentsNotAdded,
    state,
    basePath
}) => {
    const [data, setData] = useState(gradingProfileStudents);
    const [filterText, setFilterText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "lastName", direction: "ascending" });
    const [open, setOpen] = useState("false");
    const [clickedStudent, setClickedStudent] = useState();

    const { state: globalState } = useContext(GlobalContext);
    const { token } = globalState || {};
    const navigate = useNavigate();

    const filteredData = useMemo(() => {
        if (!filterText) return data;
        return data.filter((item) =>
            Object.keys(item).some((key) => item[key]?.toString().toLowerCase().includes(filterText.toLowerCase()))
        );
    }, [data, filterText]);

    /**
     * Sorts students based on the selected column and direction.
     */
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key]?.toString().toLowerCase();
            let bValue = b[sortConfig.key]?.toString().toLowerCase();
            return sortConfig.direction === "ascending" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
        });
    }, [filteredData, sortConfig]);

    /**
     * Handles sorting logic when a column header is clicked.
     * @param {string} key
     */
    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending"
        }));
    };

    const renderSortIcons = (columnKey) => {
        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === "ascending") {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up-fill" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            } else {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down-fill" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            }
        }
        return (
            <span className="icon-container">
                <i className="bi bi-caret-up" style={{ height: "16px" }} />
                <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
            </span>
        );
    };

    /**
     * Removes a student from the grading profile.
     * @param {string} student
     */
    const handleDelete = (student) => {
        const options = {
            action: "removeStudentsFromProfile",
            params: { ids: student.key },
            gradingProfileId: selectedEpr.key,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const updatedProfile = response.data.payload;

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    toast.success(`Student ${student.firstName} ${student.lastName} successfully removed.`, {
                        autoClose: 3000
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setOpen("false");
            });
    };

    const handleClear = () => {
        const options = {
            action: "clearStudentsFromProfile",
            gradingProfileId: Number(selectedEpr.key),
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    setDeltaStudents([]);
                    setStudentsNotAdded([]);
                    setShowSectionSelect(true);
                    const updatedProfile = response.data.payload;

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    toast.success("Report Card roster successfully cleared.", { autoClose: 3000 });
                }
            })
            .catch((error) => {
                toast.error("There was a problem clearing Report Card roster. Please try again later.");
                console.error(error);
            })
            .finally(() => {
                setOpen("false");
            });
    };

    /**
     * Updates the student data when the grading profile students change.
     */
    useEffect(() => {
        setData(gradingProfileStudents);
    }, [gradingProfileStudents]);

    return (
        <div className={`student-roster-table ${isGroup && "group-table"}`}>
            {!isGroup && (
                <div className="filter-wrapper">
                    <h5>Student Roster ({gradingProfileStudents.length} Students)</h5>
                    <label className="roster-table-filter-label">
                        Filter Students:
                        <input
                            className="roster-table-filter"
                            type="text"
                            placeholder="Filter students"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </label>
                </div>
            )}
            {isGroup && (
                <div className="filter-wrapper">
                    <div className="select-students-for-group">
                        Select Students for Group ({selectedStudents.length})
                    </div>
                    <label className="roster-table-filter-label">
                        Filter Students:
                        <input
                            className="roster-table-filter"
                            type="text"
                            placeholder="Filter students"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </label>
                </div>
            )}
            <div style={{ maxHeight: "350px", overflow: "scroll" }}>
                <table className="table table-striped">
                    <thead>
                        <tr style={{ position: "sticky", top: "0" }}>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("lastName")}
                                    className="table-header-button"
                                    aria-label="Sort by last name"
                                >
                                    Last Name {renderSortIcons("lastName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("firstName")}
                                    className="table-header-button"
                                    aria-label="Sort by first name"
                                >
                                    First Name {renderSortIcons("firstName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("studentNumber")}
                                    className="table-header-button"
                                    aria-label="Sort by student number"
                                >
                                    Student Number {renderSortIcons("studentNumber")}
                                </button>
                            </th>
                            <th className={isGroup ? "checkbox-header" : ""} style={{ paddingBottom: "15px" }}>
                                {isGroup ? "Select" : "Remove"}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.map((student) => {
                            const { firstName, key, lastName, studentNumber } = student;
                            return (
                                <tr key={key}>
                                    <td>{lastName}</td>
                                    <td>{firstName}</td>
                                    <td>{studentNumber}</td>
                                    <td>
                                        {isGroup ? (
                                            <>
                                                <label
                                                    className="visually-hidden"
                                                    htmlFor={`selectStudent-${studentNumber}`}
                                                >
                                                    Select {firstName}
                                                    {lastName}
                                                </label>
                                                <input
                                                    checked={selectedStudents.includes(studentNumber)}
                                                    id={`selectStudent-${studentNumber}`}
                                                    type="checkbox"
                                                    onChange={() => handleCheckboxChange(studentNumber)}
                                                />
                                            </>
                                        ) : (
                                            <button
                                                aria-label={`Remove student ${firstName} ${lastName}`}
                                                className="transparent-button"
                                                disabled={!isActiveAndIsUnreleased}
                                                onClick={() => {
                                                    setClickedStudent(student);
                                                    setOpen("confirm-delete-student");
                                                }}
                                            >
                                                <i className="bi bi-trash3" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {sortedData.length === 0 && (
                            <tr>
                                <td colSpan={4}>No students found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!isGroup && (
                <div className="back-btn-wrapper">
                    <ActionButton
                        className="ghost-button-delete"
                        disable={!isActiveAndIsUnreleased}
                        label="Clear Roster"
                        onClick={() => setOpen("clear-roster-confirmation")}
                    />
                    <ActionButton
                        label="Back to Report Card Summary"
                        onClick={() => navigate(`${basePath || "/home"}/summary`)}
                    />
                </div>
            )}
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm"
                            onClick={() => {
                                handleClear();
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Clear Roster Dialog"
                hasCloseX={false}
                id="clear-roster-confirmation"
                open={open}
                title="Clear Current Report Card Roster"
            >
                <div>
                    Please confirm that you wish to clear your current Report Card roster.
                    <span style={{ fontStyle: "italic", textDecoration: "underline", marginLeft: "4px" }}>
                        Please note, you will lose any completed work with this action.
                    </span>
                </div>
            </DcsdDialog>
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            disable={!isActiveAndIsUnreleased}
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Remove Student"
                            onClick={() => {
                                handleDelete(clickedStudent);
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Delete Student from Roster"
                hasCloseX={false}
                id="confirm-delete-student"
                open={open}
                title="Remove Student - Homeroom Roster"
            >
                <div>
                    Please confirm that you wish to remove{" "}
                    <span style={{ fontWeight: "600", color: "#CB321A" }}>
                        {clickedStudent?.firstName} {clickedStudent?.lastName}
                    </span>{" "}
                    from your current Report Card roster.
                    <br />
                    <span style={{ fontStyle: "italic", textDecoration: "underline" }}>
                        Please note, you will lose any inputted ratings or comments for this student.
                    </span>
                </div>
            </DcsdDialog>
        </div>
    );
};

StudentRosterTable.propTypes = {
    dispatch: PropTypes.func.isRequired,
    gradingProfileStudents: PropTypes.array.isRequired,
    handleCheckboxChange: PropTypes.func,
    isActiveAndIsUnreleased: PropTypes.bool,
    isGroup: PropTypes.bool,
    selectedEpr: PropTypes.object.isRequired,
    selectedStudents: PropTypes.array,
    setDeltaStudents: PropTypes.func,
    setShowSectionSelect: PropTypes.func,
    setStudentsNotAdded: PropTypes.func,
    state: PropTypes.object.isRequired,
    basePath: PropTypes.string
};

StudentRosterTable.defaultProps = {
    isActiveAndIsUnreleased: false,
    isGroup: false,
    handleCheckboxChange: null,
    selectedStudents: [],
    setDeltaStudents: null,
    setShowSectionSelect: null,
    setStudentsNotAdded: null,
    basePath: ""
};

export default StudentRosterTable;
